// src/services/courierSelectionService.js
/**
 * Service for fetching and formatting courier options for seller district
 * Flow: District → Service Areas → Branches → Courier Details
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { db } from './firebase';

const SERVICE_AREAS_COLLECTION = 'courierServiceAreas';
const COURIERS_COLLECTION = 'couriers';

/**
 * Get all available couriers for a seller's district
 * Returns courier info + branch details for each option
 * 
 * @param {string} districtCode - District code (e.g., 'Cuttack')
 * @returns {Promise<Array>} Array of courier options with branch details
 * 
 * @example
 * const options = await getCouriersByDistrict('Cuttack');
 * // Returns:
 * // [
 * //   {
 * //     courierId: 'CR-6694595',
 * //     courierName: 'Express Delivery Co',
 * //     courierLogo: 'https://...',
 * //     branchId: 'BR-0406227',
 * //     branchCode: 'CR-6694595-BR-0406227',
 * //     branchName: 'Odisha Hub',
 * //     contactPerson: 'Rajesh Kumar',
 * //     contactNumber: '9876543210',
 * //     alternateContactNumber: '9876543211',
 * //     email: 'odisha@express.com',
 * //     address: { city, stateCode, stateName, districtCode, ... },
 * //     serviceAreaId: 'CR-6694595_BR-0406227_ODISHA_CUTTACK'
 * //   },
 * //   ...
 * // ]
 */
export const getCouriersByDistrict = async (districtCode) => {
  try {
    if (!districtCode) {
      console.warn('District code is required');
      return [];
    }

    // Step 1: Query service areas for this district
    const serviceAreasQuery = query(
      collection(db, SERVICE_AREAS_COLLECTION),
      where('districtCode', '==', districtCode),
      where('active', '==', true)
    );

    const serviceAreasSnapshot = await getDocs(serviceAreasQuery);

    if (serviceAreasSnapshot.empty) {
      console.log(`No active service areas found for district: ${districtCode}`);
      return [];
    }

    const courierOptions = [];
    const processedPairs = new Set(); // Avoid duplicate courier-branch pairs

    // Step 2: For each service area, fetch courier and branch details
    for (const saDoc of serviceAreasSnapshot.docs) {
      const serviceArea = saDoc.data();
      const { courierId, branchId } = serviceArea;

      // Skip duplicate courier-branch combinations
      const pairKey = `${courierId}-${branchId}`;
      if (processedPairs.has(pairKey)) continue;
      processedPairs.add(pairKey);

      try {
        // Fetch courier details
        const courierRef = doc(db, COURIERS_COLLECTION, courierId);
        const courierSnap = await getDoc(courierRef);

        if (!courierSnap.exists()) {
          console.warn(`Courier ${courierId} not found`);
          continue;
        }

        const courierData = courierSnap.data();

        // Fetch branch details
        const branchRef = doc(
          db,
          COURIERS_COLLECTION,
          courierId,
          'branches',
          branchId
        );
        const branchSnap = await getDoc(branchRef);

        if (!branchSnap.exists()) {
          console.warn(`Branch ${branchId} not found for courier ${courierId}`);
          continue;
        }

        const branchData = branchSnap.data();

        // Skip inactive branches
        if (branchData.active === false) {
          console.log(`Branch ${branchId} is inactive, skipping`);
          continue;
        }

        // Skip inactive couriers
        if (courierData.active === false) {
          console.log(`Courier ${courierId} is inactive, skipping`);
          continue;
        }

        // Combine courier + branch info
        const courierOption = {
          courierId: courierData.courierId,
          courierName: courierData.name,
          courierLogo: courierData.logo || null,
          courierWebsite: courierData.website || null,
          branchId: branchData.branchId,
          branchCode: branchData.branchCode,
          branchName: branchData.branchName,
          contactPerson: branchData.contactPerson,
          contactNumber: branchData.contactNumber,
          alternateContactNumber: branchData.alternateContactNumber || null,
          email: branchData.email || null,
          address: branchData.address || {},
          serviceAreaId: saDoc.id,
          districtCode: serviceArea.districtCode,
          active: true,
          assignedAt: serviceArea.assignedAt
        };

        courierOptions.push(courierOption);
      } catch (err) {
        console.error(`Error processing service area ${saDoc.id}:`, err);
        continue;
      }
    }

    return courierOptions;
  } catch (error) {
    console.error('Error fetching couriers by district:', error);
    return [];
  }
};

/**
 * Get courier details with full branch information (for displaying in modal)
 * 
 * @param {string} courierId - Courier ID
 * @param {string} branchId - Branch ID
 * @returns {Promise<Object>} Formatted courier-branch details
 */
export const getCourierBranchDetails = async (courierId, branchId) => {
  try {
    // Fetch courier
    const courierRef = doc(db, COURIERS_COLLECTION, courierId);
    const courierSnap = await getDoc(courierRef);

    if (!courierSnap.exists()) {
      throw new Error(`Courier ${courierId} not found`);
    }

    // Fetch branch
    const branchRef = doc(
      db,
      COURIERS_COLLECTION,
      courierId,
      'branches',
      branchId
    );
    const branchSnap = await getDoc(branchRef);

    if (!branchSnap.exists()) {
      throw new Error(`Branch ${branchId} not found`);
    }

    const courierData = courierSnap.data();
    const branchData = branchSnap.data();

    return {
      courier: {
        courierId: courierData.courierId,
        name: courierData.name,
        logo: courierData.logo,
        website: courierData.website,
        email: courierData.email,
        contactNumber: courierData.contactNumber
      },
      branch: {
        branchId: branchData.branchId,
        branchCode: branchData.branchCode,
        branchName: branchData.branchName,
        contactPerson: branchData.contactPerson,
        contactNumber: branchData.contactNumber,
        alternateContactNumber: branchData.alternateContactNumber,
        email: branchData.email,
        address: branchData.address
      }
    };
  } catch (error) {
    console.error('Error fetching courier-branch details:', error);
    throw error;
  }
};

/**
 * Format address for display
 * @param {Object} address - Address object
 * @returns {string} Formatted address string
 */
export const formatAddress = (address) => {
  if (!address) return '';

  const parts = [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.districtName,
    address.stateName,
    address.pinCode
  ].filter(Boolean);

  return parts.join(', ');
};

/**
 * Format contact info
 * @param {Object} contact - Contact object with contactNumber, alternateContactNumber, email
 * @returns {Array<string>} Array of formatted contact strings
 */
export const formatContactInfo = (contact) => {
  const info = [];

  if (contact?.contactNumber) {
    info.push(`📞 ${contact.contactNumber}`);
  }

  if (contact?.alternateContactNumber) {
    info.push(`📞 ${contact.alternateContactNumber}`);
  }

  if (contact?.email) {
    info.push(`📧 ${contact.email}`);
  }

  return info;
};
